/**
 * viewport.ts — pan and zoom, which belong to the client and nowhere else.
 *
 * `engine-authoring-command-model.md` §21 draws the line this module lives on:
 *
 *   > Engine owns WHAT the diagram is.
 *   > Web owns HOW the diagram is displayed interactively.
 *
 * A viewport is display. It never touches a coordinate that will be sent
 * anywhere: the projection arrives in world millimetres and stays in world
 * millimetres, and this module only decides which part of it is on screen and
 * how big. That is why every function here is pure and has no notion of a
 * symbol, a port, or a plant.
 */

export interface Viewport {
  /** World mm at the top-left of the visible area. */
  originMm: { x: number; y: number };
  /** Screen pixels per millimetre. */
  scale: number;
}

export interface Size { width: number; height: number }

export const MIN_SCALE = 0.15;
export const MAX_SCALE = 24;

export function clampScale(scale: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

export function toScreen(viewport: Viewport, point: { x: number; y: number }): { x: number; y: number } {
  return {
    x: (point.x - viewport.originMm.x) * viewport.scale,
    y: (point.y - viewport.originMm.y) * viewport.scale,
  };
}

export function toWorld(viewport: Viewport, point: { x: number; y: number }): { x: number; y: number } {
  return {
    x: viewport.originMm.x + point.x / viewport.scale,
    y: viewport.originMm.y + point.y / viewport.scale,
  };
}

/**
 * Zoom about a fixed screen point.
 *
 * The point under the cursor must not move, or zooming feels like the drawing
 * is being dragged away from you. That is the whole content of the function.
 */
export function zoomAt(viewport: Viewport, screenPoint: { x: number; y: number }, factor: number): Viewport {
  const scale = clampScale(viewport.scale * factor);
  if (scale === viewport.scale) return viewport;
  const anchor = toWorld(viewport, screenPoint);
  return {
    scale,
    originMm: {
      x: anchor.x - screenPoint.x / scale,
      y: anchor.y - screenPoint.y / scale,
    },
  };
}

export function panBy(viewport: Viewport, deltaScreen: { x: number; y: number }): Viewport {
  return {
    scale: viewport.scale,
    originMm: {
      x: viewport.originMm.x - deltaScreen.x / viewport.scale,
      y: viewport.originMm.y - deltaScreen.y / viewport.scale,
    },
  };
}

/** Fit a sheet of `sheetMm` into `viewMm` pixels, with a margin, centred. */
export function fitToSheet(sheet: { widthMm: number; heightMm: number }, view: Size, marginPx = 24): Viewport {
  const usableWidth = Math.max(1, view.width - marginPx * 2);
  const usableHeight = Math.max(1, view.height - marginPx * 2);
  const scale = clampScale(Math.min(usableWidth / Math.max(1, sheet.widthMm), usableHeight / Math.max(1, sheet.heightMm)));
  return {
    scale,
    originMm: {
      x: sheet.widthMm / 2 - view.width / (2 * scale),
      y: sheet.heightMm / 2 - view.height / (2 * scale),
    },
  };
}

/** The world rectangle currently visible, for culling. */
export function visibleWorldRect(viewport: Viewport, view: Size): { x: number; y: number; w: number; h: number } {
  return {
    x: viewport.originMm.x,
    y: viewport.originMm.y,
    w: view.width / viewport.scale,
    h: view.height / viewport.scale,
  };
}
